import { Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';

const Home = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger animations after mount
    setIsLoaded(true);
  }, []);

  return (
    <>
      <style>{`
        @keyframes subtle-pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 6px 10px rgba(0,0,0,0.12);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }

        @keyframes gradient-shift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
      `}</style>
      <div style={{ backgroundColor: '#0a0a0f', color: '#ffffff', overflow: 'hidden' }}>
        {/* Hero Section */}
        <section style={{
          padding: '8rem 2rem 6rem',
          position: 'relative',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          backgroundSize: '200% 200%',
          animation: isLoaded ? 'gradient-shift 15s ease infinite' : 'none',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
          overflow: 'hidden'
        }}>
          {/* Animated background elements */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(118, 75, 162, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            animation: 'float 10s ease-in-out infinite',
            animationDelay: '1s'
          }} />

          <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1.25rem',
              backgroundColor: 'rgba(102, 126, 234, 0.15)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '50px',
              marginBottom: '2rem',
              backdropFilter: 'blur(10px)',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#a8b2ff',
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateY(0)' : 'translateY(10px)',
              transition: 'opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s'
            }}>
              <span style={{ fontSize: '1rem' }}>✨</span>
              Professional Audio Effects Platform
            </div>

            <h1 style={{
              fontSize: 'clamp(3rem, 8vw, 6rem)',
              fontWeight: '800',
              marginBottom: '1.5rem',
              letterSpacing: '-0.03em',
              lineHeight: '1.1',
              background: 'linear-gradient(135deg, #ffffff 0%, #a8b2ff 50%, #667eea 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textAlign: 'center'
            }}>
              Route Your Sound,
              <br />
              Your Way
            </h1>

            <p style={{
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
              marginBottom: '3rem',
              opacity: 0.85,
              maxWidth: '700px',
              margin: '0 auto 3rem',
              lineHeight: '1.7',
              color: '#c7d2fe',
              textAlign: 'center'
            }}>
              Discover premium audio effects, sample libraries, and presets from world-class developers. Build your custom signal chain with patchGrid's modular routing platform.
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease-out 0.6s, transform 0.8s ease-out 0.6s'
            }}>
              <Link
                to="/marketplace"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1.125rem 2.5rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
                  border: 'none',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(102, 126, 234, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.4)';
                }}
              >
                <span>Explore Marketplace</span>
                <span style={{ fontSize: '1.25rem' }}>→</span>
              </Link>
              <Link
                to="/login"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1.125rem 2.5rem',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span>Sign In</span>
              </Link>
            </div>
          </div>
        </section>

      {/* Features Section */}
      <section style={{
        padding: '6rem 2rem',
        backgroundColor: '#0f0f14',
        position: 'relative',
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 1s ease-out 0.3s'
      }}>
        {/* Grid pattern overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(102, 126, 234, 0.05) 1px, transparent 0)',
          backgroundSize: '40px 40px',
          opacity: 0.5
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '4rem',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s'
          }}>
            <div style={{
              display: 'inline-block',
              padding: '0.375rem 1rem',
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              borderRadius: '50px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#a8b2ff',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Features
            </div>
            <h2 style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #ffffff 0%, #a8b2ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '1rem',
              letterSpacing: '-0.02em'
            }}>
              Everything You Need
            </h2>
            <p style={{
              fontSize: '1.25rem',
              color: '#9ca3af',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Professional tools crafted for modern music production
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            <FeatureCard
              icon="🎸"
              title="Premium Effects"
              description="High-quality audio effects pedals from professional developers worldwide"
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              index={0}
              isLoaded={isLoaded}
            />
            <FeatureCard
              icon="🎵"
              title="Sample Libraries"
              description="Curated collections of drums, instruments, and sound effects for every genre"
              gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              index={1}
              isLoaded={isLoaded}
            />
            <FeatureCard
              icon="🎨"
              title="Preset Packs"
              description="Ready-to-use configurations from top sound designers and producers"
              gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
              index={2}
              isLoaded={isLoaded}
            />
            <FeatureCard
              icon="☁️"
              title="Cloud Sync"
              description="Access your purchases and presets from any device, anytime, anywhere"
              gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              index={3}
              isLoaded={isLoaded}
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{
        padding: '6rem 2rem',
        backgroundColor: '#1a1a2e',
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 0.8s ease-out 0.9s',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '2rem',
            textAlign: 'center'
          }}>
            <StatCard number="500+" label="Premium Pedals" />
            <StatCard number="10K+" label="Audio Samples" />
            <StatCard number="1K+" label="Preset Packs" />
            <StatCard number="50K+" label="Happy Users" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '8rem 2rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #0f3460 0%, #16213e 50%, #1a1a2e 100%)',
        color: 'white',
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? 'scale(1)' : 'scale(0.95)',
        transition: 'opacity 0.8s ease-out 1.1s, transform 0.8s ease-out 1.1s',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Gradient orbs */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(102, 126, 234, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(118, 75, 162, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-block',
            padding: '0.375rem 1rem',
            backgroundColor: 'rgba(102, 126, 234, 0.15)',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '50px',
            marginBottom: '2rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#a8b2ff',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Get Started Today
          </div>
          <h2 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: '800',
            marginBottom: '1.5rem',
            letterSpacing: '-0.02em',
            lineHeight: '1.1'
          }}>
            Ready to Elevate Your Sound?
          </h2>
          <p style={{
            fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
            marginBottom: '3rem',
            opacity: 0.85,
            color: '#c7d2fe',
            maxWidth: '600px',
            margin: '0 auto 3rem',
            lineHeight: '1.7'
          }}>
            Join thousands of producers and musicians routing their perfect sound with patchGrid
          </p>
          <Link
            to="/marketplace"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1.25rem 3rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '14px',
              fontSize: '1.25rem',
              fontWeight: '600',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              boxShadow: '0 15px 35px rgba(102, 126, 234, 0.4)',
              border: 'none'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 20px 45px rgba(102, 126, 234, 0.5)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 15px 35px rgba(102, 126, 234, 0.4)';
            }}
          >
            <span>Explore Marketplace</span>
            <span style={{ fontSize: '1.5rem', fontWeight: '300' }}>→</span>
          </Link>
        </div>
      </section>
      </div>
    </>
  );
};

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
  gradient: string;
  index: number;
  isLoaded: boolean;
}

function FeatureCard({ icon, title, description, gradient, index, isLoaded }: FeatureCardProps) {
  const delay = 0.5 + (index * 0.1); // Stagger animation by 0.1s for each card
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
        borderRadius: '24px',
        padding: '2.5rem',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}s`,
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.4)';
        e.currentTarget.style.boxShadow = '0 20px 40px rgba(102, 126, 234, 0.15)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = isLoaded ? 'translateY(0)' : 'translateY(30px)';
        e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Gradient overlay on hover */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: gradient,
        opacity: isHovered ? 0.05 : 0,
        transition: 'opacity 0.4s ease',
        pointerEvents: 'none'
      }} />

      <div style={{
        width: '5rem',
        height: '5rem',
        borderRadius: '20px',
        background: gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2.5rem',
        marginBottom: '2rem',
        animationName: isLoaded ? 'float' : 'none',
        animationDuration: '3s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        animationDelay: `${index * 0.2}s`,
        boxShadow: `0 10px 30px ${gradient.match(/#[a-f0-9]{6}/i)?.[0] || '#667eea'}30`,
        position: 'relative',
        zIndex: 1
      }}>
        {icon}
      </div>
      <h3 style={{
        fontSize: '1.75rem',
        fontWeight: '700',
        color: '#ffffff',
        marginBottom: '1rem',
        letterSpacing: '-0.01em',
        position: 'relative',
        zIndex: 1
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '1.0625rem',
        color: '#9ca3af',
        lineHeight: '1.7',
        position: 'relative',
        zIndex: 1
      }}>
        {description}
      </p>
    </div>
  );
}

interface StatCardProps {
  number: string;
  label: string;
}

function StatCard({ number, label }: StatCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '2.5rem 2rem',
        background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.4) 0%, rgba(22, 33, 62, 0.4) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-5px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 15px 30px rgba(102, 126, 234, 0.2)' : 'none'
      }}
    >
      <div style={{
        fontSize: 'clamp(2.5rem, 4vw, 4rem)',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #ffffff 0%, #a8b2ff 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: '0.75rem',
        letterSpacing: '-0.02em'
      }}>
        {number}
      </div>
      <div style={{
        fontSize: '1.125rem',
        color: '#9ca3af',
        fontWeight: '600',
        letterSpacing: '0.02em'
      }}>
        {label}
      </div>
    </div>
  );
}

export default Home;
